'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const processSteps = [
  'Company',
  'Opportunity Review',
  'Investor Matching',
  'Investor Interest',
  'Founder–Investor Meeting',
  'Due Diligence',
  'Investment Discussion',
  'Closing',
];

const investorBenefits = [
  {
    title: 'Curated Deal Flow',
    desc: 'Receive access to selected opportunities aligned with your interests and investment thesis.',
  },
  {
    title: 'Direct Founder Access',
    desc: 'Meet founders and understand their businesses directly, without intermediary filters.',
  },
  {
    title: 'Early Access',
    desc: 'Engage with companies during fundraising rather than after a round is already broadly marketed.',
  },
  {
    title: 'Co-Investment',
    desc: 'Participate alongside other investors where appropriate and mandates align.',
  },
];

export default function SyndicateSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="syndicate" className="py-24 px-6 border-t border-primary/10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="section-label scroll-fade mb-5">Syndicate</p>
          <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-4">
            Meet investors who are{' '}
            <span className="font-serif italic text-primary">
              interested in what you&apos;re building.
            </span>
          </h2>
          <p className="text-sm text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2">
            The Aregn Capital Syndicate connects selected companies with a pool of investors who
            actively evaluate opportunities — matched by interests, stage and mandate.
          </p>
        </div>

        {/* Process flow */}
        <div className="mb-20 scroll-fade">
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-6 text-center">
            The Process
          </p>
          <div className="flex flex-wrap justify-center gap-0">
            {processSteps?.map((step, i) => (
              <div key={step} className="flex items-center">
                <div className="flex flex-col items-center px-3 py-2">
                  <div className="w-8 h-8 rounded-full border border-primary/40 bg-primary/8 flex items-center justify-center text-xs font-semibold text-primary mb-2">
                    {i + 1}
                  </div>
                  <span className="text-xs text-muted-foreground text-center max-w-20 leading-tight">
                    {step}
                  </span>
                </div>
                {i < processSteps?.length - 1 && (
                  <div className="w-6 h-px bg-primary/25 mb-5 hidden sm:block" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Dual panel — Founders + Investors */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Founders Panel */}
          <div className="bento-card rounded-3xl p-8 scroll-fade flex flex-col">
            <div className="mb-6">
              <span className="eyebrow-badge mb-4 inline-flex">For Founders</span>
              <h3 className="text-xl font-semibold text-foreground mt-4 mb-3">
                Your company. The right investor conversations.
              </h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
                Companies seeking capital can submit their opportunity to Aregn Capital. We evaluate
                based on stage, sector, business model, traction, market, capital requirement and
                investor fit.
              </p>
            </div>

            <div className="space-y-2 mb-6 flex-1">
              {[
                'Stage & sector fit',
                'Business model assessment',
                'Traction review',
                'Investor matching',
                'Relevant introductions',
              ]?.map((item) => (
                <div key={item} className="flex items-center gap-3 py-2 border-b border-primary/8">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="text-primary shrink-0"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="text-sm text-foreground/80">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground italic mb-6 leading-relaxed">
              An introduction does not guarantee investment. Investment decisions are made
              independently by individual investors.
            </p>

            <Link
              href="/contact"
              className="w-full py-3.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase text-center hover:bg-accent transition-all duration-300"
            >
              Submit Your Company
            </Link>
          </div>

          {/* Investors Panel */}
          <div className="bento-card rounded-3xl p-8 scroll-fade scroll-fade-delay-2 flex flex-col">
            <div className="mb-6">
              <span className="eyebrow-badge mb-4 inline-flex">For Investors</span>
              <h3 className="text-xl font-semibold text-foreground mt-4 mb-3">
                Access opportunities. Build relationships.{' '}
                <span className="font-serif italic text-primary">Invest with conviction.</span>
              </h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
                Aregn Capital works with investors seeking access to emerging companies and growth
                opportunities. Participate according to your sector preferences, geography, stage
                and investment thesis.
              </p>
            </div>

            {/* BENTO AUDIT: 4 benefit cards, 2x2 grid */}
            {/* Row 1: [col-1: Curated cs-1] [col-2: Direct cs-1] */}
            {/* Row 2: [col-1: Early cs-1] [col-2: Co-invest cs-1] */}
            {/* Placed 4/4 ✓ */}
            <div className="grid grid-cols-2 gap-3 mb-6 flex-1">
              {investorBenefits?.map((benefit) => (
                <div
                  key={benefit?.title}
                  className="rounded-xl border border-primary/12 bg-primary/4 p-4"
                >
                  <h4 className="text-sm font-semibold text-foreground mb-1">{benefit?.title}</h4>
                  <p className="text-xs text-muted-foreground font-light leading-relaxed">
                    {benefit?.desc}
                  </p>
                </div>
              ))}
            </div>

            <Link
              href="/contact"
              className="w-full py-3.5 rounded-full border border-primary/30 text-primary text-sm font-semibold tracking-widest uppercase text-center hover:bg-primary/10 hover:border-primary/50 transition-all duration-300"
            >
              Join the Investor Network
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
